import { useState } from 'react'

import { Box, SortDirection, Table, Text } from '@factorialco/deprecated-design-system'
import { EmployeeTimePeriodDetailsFragment } from 'generated/resources/graphql'
import { orderBy, range } from 'lodash'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'

import {
  MAX_TO_SHOW,
  RangeOptionsEnum,
  sortByName,
} from 'modules/shift_management/components/ShiftManagement/TimeInsightsSidepanel/Content/utils'

import EmployeeData from './EmployeeData'
import LoadingSkeleton from './LoadingSkeleton'

const computedEmployees = createComputed(
  (data: EmployeeTimePeriodDetailsFragment[], direction: SortDirection) => {
    return orderBy(data, (item) => sortByName(item), [direction])
  }
)

interface Props {
  timePeriodDetails: EmployeeTimePeriodDetailsFragment[]
  rangeType: string
  loading: boolean
  convertHours: boolean
}

const PlannedTimeTable = ({ timePeriodDetails, rangeType, loading, convertHours }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [direction, setDirection] = useState<SortDirection>('asc')
  const [attribute, setAttribute] = useState<string>('employeeFullName')

  const onSort = (sortAttribute: string, sortDirection: SortDirection) => {
    setDirection(sortDirection)
    setAttribute(sortAttribute)
  }

  const orderedData = orderBy(timePeriodDetails, (item) => sortByName(item), ['asc'])
  const sortedEmployees = computedEmployees(orderedData, direction).get()
  const employeesWithData = isExpanded ? sortedEmployees : sortedEmployees.slice(0, MAX_TO_SHOW)
  const employeesCount = range(employeesWithData.length)

  const employeeHeader = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.employee_header'
  )

  const stackedBarsHeader = () => {
    switch (rangeType) {
      case RangeOptionsEnum.week:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.header_week'
        )
      case RangeOptionsEnum.thisWeek:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.header_week'
        )
      case RangeOptionsEnum.month:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.header_month'
        )
      case RangeOptionsEnum.thisMonth:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.header_month'
        )
      case RangeOptionsEnum.yearToDate:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.header_year_to_date'
        )
      default:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.header'
        )
    }
  }

  const showMore = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.show_more'
  )
  const showLess = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.show_less'
  )

  const optionsBottom =
    sortedEmployees.length > MAX_TO_SHOW
      ? () => {
          return (
            <Box width="full">
              <Box alignSelf="center" cursor="pointer" onClick={() => setIsExpanded(!isExpanded)}>
                <Text.Normal size="200" weight="semibold">
                  {isExpanded ? showLess : showMore}
                </Text.Normal>
              </Box>
            </Box>
          )
        }
      : undefined

  return (
    <Table optionsBottom={optionsBottom}>
      <Table.Head>
        <Table.Row>
          <Table.SortableHeadCell
            onSort={onSort}
            sortAttribute={attribute}
            sortDirection={direction}
            attribute={attribute}
            colSpan={3}
          >
            {employeeHeader}
          </Table.SortableHeadCell>
          <Table.HeadCell colSpan={7}>{stackedBarsHeader()}</Table.HeadCell>
          <Table.HeadCell colSpan={2} />
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {loading
          ? employeesCount.map((i: number) => (
              <Table.Row key={i}>
                <LoadingSkeleton />
              </Table.Row>
            ))
          : employeesWithData.map((employeeData) => (
              <Table.Row key={employeeData.id}>
                <EmployeeData
                  employeeData={employeeData}
                  rangeType={rangeType}
                  convertHours={convertHours}
                />
              </Table.Row>
            ))}
      </Table.Body>
    </Table>
  )
}

export default observer(PlannedTimeTable)
